import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { speak, speakAll, stopSpeaking, words } from './speech';
import '../assets/scss/challenge.scss';
import '../assets/scss/v1plus.scss';

const COLORS = ['#FFB800', '#4ECB71', '#FF6B6B', '#3B82F6', '#8B5CF6'];
const TRACKS = [{ id: 'travel', label: '여행' }, { id: 'life', label: '일상' }, { id: 'grammar', label: '문법' }];

// v1 detail-style dropdown
const Select = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = options.find(o => o.id === value) || options[0];
  return (
    <div className="select-wrap">
      <div className={`select ${open ? 'active' : ''}`}>
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}>{current.label}</button>
        <ul style={{ display: open ? 'block' : 'none' }}>
          {options.map(o => <li key={o.id} className={o.id === value ? 'active' : ''} onClick={() => { setOpen(false); onChange(o.id); }}>{o.label}</li>)}
        </ul>
      </div>
    </div>
  );
};

// K. Talk list: v1 curriculum cards, track chosen with the dropdown (kept in the URL so back returns to it)
export const Talk = () => {
  const [params, setParams] = useSearchParams();
  const track = params.get('t') || 'travel';
  const [units, setUnits] = useState(null);
  const [grammar, setGrammar] = useState(null);
  const [today, setToday] = useState(null);

  useEffect(() => {
    axios.get('/api/learn/talk').then(res => setUnits(res.data)).catch(() => setUnits([]));
    axios.get('/api/learn/grammar').then(res => setGrammar(res.data)).catch(() => setGrammar([]));
    axios.get('/api/learn/home').then(res => setToday(res.data)).catch(() => { });
  }, []);

  const list = track === 'grammar'
    ? (grammar || []).map(g => ({ key: g.no, to: `/Grammar/${g.no}`, title: `${g.no} · ${g.title}`, sub: g.level }))
    : (units || []).filter(u => u.track === track).map(u => ({
      key: u.unit, to: `/Talk/${u.track}/${u.unit}`, title: `${u.unit} · ${u.unit_title}`,
      sub: today && today.track === u.track && today.topic === u.unit_title ? '오늘의 레슨에서 배우는 중' : `${u.n}문장`,
    }));
  const loading = track === 'grammar' ? !grammar : !units;

  return (
    <Layout header={{ title: '회화', Backbtn: true, link: '/Main' }} containName='result v1-keep'>
      <div>
        <Select value={track} options={TRACKS} onChange={(t) => setParams({ t }, { replace: true })} />
        <p className="v1-guide" style={{ textAlign: 'left' }}>
          {track === 'travel' ? '뉴욕 여행을 기준으로 공항부터 귀국까지 순서대로 담았어요.' : track === 'life' ? '자기소개부터 의견 말하기까지, 중급 회화에 필요한 표현이에요.' : '초급부터 중급까지 꼭 필요한 문법 50개예요.'}
        </p>
        <div className="v1-cards">
          {loading ? <p className="v1-guide">불러오는 중...</p> : list.map((u, n) => (
            <Link key={u.key} to={u.to} style={{ borderLeftColor: COLORS[n % COLORS.length] }}>
              <strong>{u.title}</strong><small>{u.sub}</small>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
};

const KINDS = [{ id: 'all', label: '전체' }, { id: 'expr', label: '표현' }, { id: 'sent', label: '문장' }, { id: 'talk', label: '대화' }];
const plain = (s) => s.replace(/^[^:]+: /, '').replace(/~/g, 'something');

// L. One situation: v1 detail list; dropdown filters expressions / sentences / dialogue, floating menu for practice
export const TalkUnit = () => {
  const { track, unit } = useParams();
  const [rows, setRows] = useState(null);
  const [kind, setKind] = useState('all');
  const [hideKr, setHideKr] = useState(false);
  const [fab, setFab] = useState(false);
  const playing = useRef(false);

  useEffect(() => { axios.get(`/api/learn/talk/${track}/${unit}`).then(res => setRows(res.data)).catch(() => setRows([])); }, [track, unit]);
  useEffect(() => () => { playing.current = false; stopSpeaking(); }, []);

  // Read the whole dialogue aloud, line by line
  const playDialogue = () => {
    setFab(false);
    const lines = (rows || []).filter(r => r.kind === 'talk').map(r => plain(r.en));
    if (!lines.length) return;
    playing.current = true;
    speakAll(lines, () => playing.current);
  };

  const shown = (rows || []).filter(r => kind === 'all' || r.kind === kind);
  const title = rows && rows[0] ? rows[0].unit_title : '회화';

  return (
    <Layout header={{ title, Backbtn: true, link: `/Talk?t=${track}` }} containName='result v1-keep'>
      <Select value={kind} options={KINDS} onChange={setKind} />
      <div className="v1-list">
        {!rows ? <p className="v1-guide">불러오는 중...</p> : !rows.length ? <p className="v1-guide">내용을 찾을 수 없어요.</p> : shown.map((r, n) => (
          <React.Fragment key={n}>
            {(n === 0 || shown[n - 1].kind !== r.kind) && kind === 'all' && (
              <p className="v1-section">{{ expr: '핵심 표현', sent: '상황 문장', talk: '대화 · 주황 줄이 내 대사예요' }[r.kind]}</p>
            )}
            <div className={`v1-row ${r.kind === 'talk' && r.en.startsWith('You:') ? 'you' : ''}`}>
              <span className="t">
                <span className="en">{r.en}</span>
                {!hideKr && <span className="m">{r.kr}</span>}
                {r.tip && <span className="tip">{r.tip}</span>}
              </span>
              <button type="button" className="v1-snd" aria-label="듣기" onClick={() => speak(plain(r.en))} />
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className="v1-fab-wrap">
        {fab && <div className="v1-fab-dim" onClick={() => setFab(false)} />}
        {fab && (
          <div className="v1-fab-menu">
            <button type="button" onClick={() => { setHideKr(!hideKr); setFab(false); }}>{hideKr ? '한국어 보이기' : '한국어 가리기'}<i>가</i></button>
            <button type="button" onClick={playDialogue}>대화 전체 듣기<i>▶</i></button>
            <button type="button" onClick={() => { setKind('talk'); setHideKr(true); setFab(false); }}>내 대사 연습하기<i>🎙</i></button>
          </div>
        )}
        <button type="button" className={`v1-fab ${fab ? 'on' : ''}`} aria-label="메뉴" onClick={() => setFab(!fab)}>+</button>
      </div>
    </Layout>
  );
};

// Grammar pattern: rule box, examples in the v1 list, fill-in-the-blank quizzes
export const Grammar = () => {
  const { no } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`/api/learn/grammar/${no}`).then(res => setRows(res.data)).catch(() => setRows([])); }, [no]);
  const rule = rows && rows.find(r => r.kind === 'rule');
  const n = parseInt(no);

  return (
    <Layout header={{ title: '문법', Backbtn: true, link: '/Talk?t=grammar' }} containName='result v1-keep'>
      {!rows ? <p className="v1-guide">불러오는 중...</p> : !rows.length ? <p className="v1-guide">내용을 찾을 수 없어요.</p> : (
        <div style={{ paddingBottom: 40 }}>
          <p className="v1-section" style={{ marginTop: 6 }}>{rows[0].level} · {rows[0].title}</p>
          {rule && <div className="v1-rule"><strong>{rule.en}</strong><p>{rule.kr}</p></div>}
          <p className="v1-section">예문</p>
          {rows.filter(r => r.kind === 'ex').map((r, k) => (
            <div key={k} className="v1-row">
              <span className="t"><span className="en">{r.en}</span><span className="m">{r.kr}</span>{r.answer && <span className="tip">{r.answer}</span>}</span>
              <button type="button" className="v1-snd" aria-label="듣기" onClick={() => speak(r.en)} />
            </div>
          ))}
          <p className="v1-section">빈칸 채우기</p>
          {rows.filter(r => r.kind === 'quiz').map((r, k) => <Quiz key={`${no}-${k}`} q={r} />)}
          <div className="v1-rows" style={{ marginTop: 24 }}>
            {n > 1 && <button type="button" onClick={() => navigate(`/Grammar/${n - 1}`)}><span className="t"><strong>이전 문법</strong></span><span className="go" style={{ transform: 'rotate(-135deg)' }} /></button>}
            {n < 50 && <button type="button" onClick={() => navigate(`/Grammar/${n + 1}`)}><span className="t"><strong>다음 문법</strong></span><span className="go" /></button>}
          </div>
        </div>
      )}
    </Layout>
  );
};

const Quiz = ({ q }) => {
  const [v, setV] = useState('');
  const [res, setRes] = useState(null);
  const check = () => setRes(words(v).join(' ') === words(q.answer).join(' '));
  return (
    <div className="v1-quiz">
      <p className="q">{q.en}</p>
      <p className="k">{q.kr}</p>
      <div className="row">
        <input value={v} onChange={e => { setV(e.target.value); setRes(null); }} placeholder="정답 입력" aria-label="빈칸 정답"
          autoComplete="off" autoCapitalize="off" spellCheck="false" onKeyDown={e => e.key === 'Enter' && v.trim() && check()} />
        <button type="button" disabled={!v.trim()} onClick={check}>확인</button>
      </div>
      {res !== null && <p className={`res ${res ? 'ok' : 'no'}`}>{res ? '정답이에요!' : `정답: ${q.answer}`}</p>}
    </div>
  );
};
